import { Separator, Text } from '@cuemath/web-comps';
import React from 'react';
import {
  Button,
  Dropdown,
  Icon,
  Input,
  Loader,
  Modal,
  ModalActions,
  ModalContent,
  ModalDescription,
  ModalHeader,
  Segment,
} from 'semantic-ui-react';
import { Col, Row } from '@cuemath/comps';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import {
  useGetEvalCoachCandidates,
  useGetEvalCoachEligibility,
  usePostEvalManualCoachAssign,
} from 'actions/eval-failed-mappings';
import { FAILURE_REASONS, formatAge, isUuid } from '../constants';
import {
  PLAN_TIERS,
  PLAN_TIER_OPTIONS,
  epochsToIstInputs,
  gradeNeedsTier,
  isPastIstInput,
  istLocalInputToEpoch,
} from '../common/constants';
import HalfHourPicker from '../common/half-hour-picker';

const ContextCard = styled(Col)`
  background: ${COLORS.WILD_SAND};
  border-radius: 8px;
`;

const ContextRow = styled(Row)`
  padding: 12px 16px;
`;

const SlotField = styled.div`
  margin-bottom: 6px;
`;
const ContextRowTight = styled(Row)`
  padding: 0 16px 12px;
`;

const GateRow = styled(Row)`
  padding: 4px 0;
`;

const FUNNEL_STEPS = [
  { key: 'first_class_trained', label: 'trained' },
  { key: 'license', label: 'licence' },
  { key: 'active', label: 'active' },
  { key: 'has_profile', label: 'profile' },
  { key: 'capacity', label: 'capacity' },
  { key: 'female_only', label: 'female' },
  { key: 'available', label: 'available' },
];

const describeCandidate = candidate =>
  `${candidate.name || candidate.teacher_id} — ${candidate.open_allocations} open`;

const AssignCoach = ({ failure, closeModal, onAssigned }) => {
  const [selectedTeacherId, setSelectedTeacherId] = React.useState(null);
  const [coachIdInput, setCoachIdInput] = React.useState('');
  const [checkedCoachId, setCheckedCoachId] = React.useState(null);
  const [assignError, setAssignError] = React.useState(null);
  const [assignResult, setAssignResult] = React.useState(null);
  // Pre-filled with what the parent already chose: the common case is keeping the
  // slots and only changing the coach, so an empty picker would invite retyping.
  const [slots, setSlots] = React.useState(() =>
    epochsToIstInputs(failure.requested_slots),
  );
  // Empty means "no override" -- the backend then resolves the live tier.
  const [tier, setTier] = React.useState('');
  const [editingSlots, setEditingSlots] = React.useState(
    Boolean(failure.assigned_teacher_id),
  );
  const isCoachChange = Boolean(failure.assigned_teacher_id);

  const intelEnrollmentId = failure.intel_enrollment_id;
  const slotEpochs = React.useMemo(
    () => slots.map(istLocalInputToEpoch).filter(Boolean),
    [slots],
  );
  // A slot in the past can never be booked, so it must not reach the availability
  // check or the assign.
  const hasPastSlot = React.useMemo(() => slots.some(isPastIstInput), [slots]);
  // Only send an override once it differs, so the untouched case keeps reading the
  // demo and cannot disagree with what the parent actually booked.
  const slotsChanged = React.useMemo(
    () =>
      JSON.stringify(slotEpochs) !==
      JSON.stringify((failure.requested_slots || []).map(Number)),
    [slotEpochs, failure.requested_slots],
  );
  // Slots are applied on confirm, never per keystroke: each edit would otherwise
  // fire a six-gate evaluation per coach and the list would churn mid-decision.
  const [confirmedSlots, setConfirmedSlots] = React.useState(null);
  const slotsConfirmed = Boolean(confirmedSlots && confirmedSlots.length);
  const slotQuery = React.useMemo(
    () => ({
      tier: tier || undefined,
      ...(slotsConfirmed
        ? { requested_slots: confirmedSlots }
        : {}),
    }),
    [tier, slotsConfirmed, confirmedSlots],
  );
  const reason = FAILURE_REASONS[failure.failure_reason] || FAILURE_REASONS.UNKNOWN;

  const {
    data: candidatesData,
    get: getCandidates,
    isProcessing: isLoadingCandidates,
    isProcessingFailed: candidatesFailed,
  } = useGetEvalCoachCandidates();

  const {
    data: eligibilityData,
    get: getEligibility,
    isProcessing: isCheckingCoach,
    isProcessingFailed: eligibilityFailed,
  } = useGetEvalCoachEligibility();

  const { post: postAssign, isProcessing: isAssigning } =
    usePostEvalManualCoachAssign({
      onComplete: (error, data) => {
        if (error) {
          setAssignError(error || 'Assign failed');

          return;
        }
        setAssignError(null);
        setAssignResult(data || {});
      },
    });

  React.useEffect(() => {
    getCandidates(intelEnrollmentId, slotQuery);
  }, [intelEnrollmentId, slotQuery]);

  const candidates = React.useMemo(
    () => (candidatesData || {}).candidates || [],
    [candidatesData],
  );
  const blockedCount = (candidatesData || {}).blocked_count || 0;
  // The queue row's grade is the one the mapping was requested for; a later
  // grade edit leaves it behind, and eligibility runs on the live student. Until
  // that arrives there is no grade to show: falling back to the row's would
  // print the stale one and then flip, and flash a tier control that may not
  // belong to this student.
  const liveStudent = (candidatesData || {}).student || {};
  const grade = liveStudent.grade;
  const needsTier = gradeNeedsTier(grade);
  // Matched to the option casing so the dropdown shows a selection.
  const effectiveTier = React.useMemo(() => {
    const resolved = tier || liveStudent.plan_tier;

    return (
      PLAN_TIERS.find(
        option => option.toLowerCase() === String(resolved || '').toLowerCase(),
      ) || ''
    );
  }, [tier, liveStudent.plan_tier]);

  const options = React.useMemo(
    () =>
      candidates.map(candidate => ({
        key: candidate.teacher_id,
        value: candidate.teacher_id,
        text: describeCandidate(candidate),
      })),
    [candidates],
  );

  const trimmedCoachId = coachIdInput.trim();
  const canCheckCoach = isUuid(trimmedCoachId) && !isCheckingCoach;

  // Editing the box after a check must not let a stale pass through.
  const checkedResult =
    checkedCoachId && checkedCoachId === trimmedCoachId ? eligibilityData : null;
  const isCheckedCoachEligible = Boolean(checkedResult && checkedResult.is_eligible);

  const handleSelect = React.useCallback((_, { value }) => {
    setSelectedTeacherId(value);
    setAssignError(null);
  }, []);

  const handleCoachIdChange = React.useCallback((_, { value }) => {
    setCoachIdInput(value);
    setAssignError(null);
  }, []);

  const handleCheckCoach = React.useCallback(() => {
    setAssignError(null);
    setCheckedCoachId(trimmedCoachId);
    getEligibility(intelEnrollmentId, {
      teacherId: trimmedCoachId,
      ...slotQuery,
    });
  }, [trimmedCoachId, intelEnrollmentId, slotQuery]);

  const handleAssign = React.useCallback(
    teacherId => {
      setAssignError(null);
      // Naming the outgoing coach lets the gateway release their provisional hold;
      // without it a replaced coach keeps slots they no longer own.
      const payload = { teacher_id: teacherId };

      if (isCoachChange) {
        payload.previous_teacher_id = failure.assigned_teacher_id;
      }
      // Only sent when actually changed, so an untouched assign keeps reading the
      // demo and cannot overwrite the parent's slots with a re-render of them.
      if (slotsConfirmed && slotEpochs.length) {
        payload.requested_slots = slotEpochs;
      }
      if (tier) payload.plan_tier = tier;
      postAssign(payload, intelEnrollmentId);
    },
    [
      intelEnrollmentId,
      failure.assigned_teacher_id,
      slotsConfirmed,
      slotEpochs,
      tier,
      isCoachChange,
    ],
  );

  const handleDone = React.useCallback(() => {
    if (assignResult && onAssigned) onAssigned();
    closeModal();
  }, [assignResult, onAssigned, closeModal]);

  const renderSuccess = () => (
    <Segment>
      <Text variant="body1" color={COLORS.DARK_GREEN}>
        <Icon name="check circle" /> {assignResult.teacher_name} is now this
        student&apos;s coach.
      </Text>
      <Separator height={12} transparent />
      <Text variant="body2">
        Slots held for 48h: {assignResult.hold_placed ? 'yes' : 'no'}
      </Text>
      <Separator height={4} transparent />
      <Text variant="body2">
        Parent and coach notified: {assignResult.comms_sent ? 'yes' : 'no'}
      </Text>
      {!assignResult.hold_placed && (
        <>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.RUSTY_RED}>
            Nothing is reserving the coach&apos;s time, so another student can
            take these slots. Place the hold in the tutor calendar.
          </Text>
        </>
      )}
      {!assignResult.comms_sent && (
        <>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.RUSTY_RED}>
            Neither the parent nor the coach knows yet — tell them directly.
          </Text>
        </>
      )}
    </Segment>
  );

  const renderUnfixableNote = () => (
    <>
      <Segment>
        <Text variant="body2" color={COLORS.RUSTY_RED}>
          <Icon name="info circle" /> {reason.detail}
        </Text>
      </Segment>
      <Separator height={12} transparent />
    </>
  );

  const renderEligibleList = () => {
    if (isLoadingCandidates) {
      return (
        <Segment>
          <Col alignItems="center" gutter={0}>
            <Loader active inline="centered" size="small" />
            <Separator height={12} transparent />
            <Text variant="body2">
              Checking every coach against this student&apos;s slots…
            </Text>
            <Separator height={4} transparent />
            <Text variant="body3" color={COLORS.GRAY}>
              This runs six checks per coach and can take a few seconds.
            </Text>
          </Col>
        </Segment>
      );
    }
    if (candidatesFailed) {
      return (
        <Segment>
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            <Icon name="warning sign" /> Could not load the coach list.
          </Text>
          <Separator height={4} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            This is a failed request, not an empty pool — reopen to retry.
          </Text>
        </Segment>
      );
    }
    if (!candidates.length) {
      return (
        <Segment>
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            <Icon name="info circle" />{' '}
            {blockedCount
              ? `All ${blockedCount} coach${
                  blockedCount === 1 ? '' : 'es'
                } in the pool failed at least one check.`
              : 'No coach is in the pool for this student at all.'}
          </Text>
          <Separator height={8} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            Check a specific coach below to see exactly which check they fail.
          </Text>
        </Segment>
      );
    }

    return (
      <>
        <Text variant="body3" color={COLORS.GRAY}>
          Only coaches clearing every check are listed, least loaded first.
        </Text>
        <Separator height={8} transparent />
        <Dropdown
          floating
          fluid
          selection
          selectOnBlur={false}
          options={options}
          value={selectedTeacherId}
          placeholder="Choose a coach"
          onChange={handleSelect}
        />
      </>
    );
  };

  const renderGateResults = () => {
    if (isCheckingCoach) {
      return <Loader active inline="centered" />;
    }
    if (eligibilityFailed) {
      return (
        <>
          <Separator height={12} transparent />
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            <Icon name="warning sign" /> Could not check that coach. Confirm the
            id is a coach id and try again.
          </Text>
        </>
      );
    }
    if (!checkedResult) return null;

    return (
      <>
        <Separator height={12} transparent />
        <Segment>
          <Text variant="body2">
            {checkedResult.name}{' '}
            {checkedResult.is_eligible ? (
              <Text variant="body2" color={COLORS.DARK_GREEN}>
                — clears every check
              </Text>
            ) : (
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                — cannot take this student
              </Text>
            )}
          </Text>
          <Separator height={8} transparent />
          {(checkedResult.gates || []).map(gate => (
            <GateRow key={gate.gate} alignItems="center">
              <Text
                variant="body3"
                color={gate.passed ? COLORS.DARK_GREEN : COLORS.RUSTY_RED}
              >
                <Icon name={gate.passed ? 'check' : 'close'} /> {gate.label}
              </Text>
            </GateRow>
          ))}
        </Segment>
      </>
    );
  };

  const renderCoachIdCheck = () => (
    <>
      <Separator height={20} transparent />
      <Text variant="body2">Check a specific coach</Text>
      <Separator height={6} transparent />
      <Text variant="body3" color={COLORS.GRAY}>
        Paste a coach id to see which checks they pass. Assigning only becomes
        available if they pass all of them.
      </Text>
      <Separator height={8} transparent />
      <Input
        fluid
        action={{
          content: 'Check Eligibility',
          onClick: handleCheckCoach,
          disabled: !canCheckCoach,
          loading: isCheckingCoach,
        }}
        placeholder="Coach id"
        value={coachIdInput}
        onChange={handleCoachIdChange}
      />
      {renderGateResults()}
    </>
  );

  const renderBody = () => {
    if (assignResult) return renderSuccess();

    return (
      <>
        {!isCoachChange && !reason.fixable && renderUnfixableNote()}
        {renderEligibleList()}
        {renderCoachIdCheck()}
      </>
    );
  };

  // The student's grade decides whether the tier control belongs on screen at
  // all, so nothing is rendered until it lands -- a partial modal would shift
  // under the admin as each piece arrives.
  const renderLoading = () => (
    <Segment basic>
      <Col alignItems="center" gutter={0}>
        <Loader active inline="centered" size="small" />
        <Separator height={12} transparent />
        <Text variant="body2">Loading this student&apos;s details…</Text>
        <Separator height={4} transparent />
        <Text variant="body3" color={COLORS.GRAY}>
          Checking every coach against their slots can take a few seconds.
        </Text>
      </Col>
    </Segment>
  );

  const renderActions = () => {
    if (assignResult) {
      return (
        <Button color="black" onClick={handleDone}>
          Done
        </Button>
      );
    }

    if (!grade) {
      return (
        <Button basic onClick={closeModal}>
          Cancel
        </Button>
      );
    }

    return (
      <>
        <Button basic onClick={closeModal}>
          Cancel
        </Button>
        <Button basic onClick={() => setEditingSlots(current => !current)}>
          {editingSlots ? 'Hide slots' : 'Change slots'}
        </Button>
        {isCheckedCoachEligible && (
          <Button
            color="black"
            loading={isAssigning}
            disabled={isAssigning}
            onClick={() => handleAssign(checkedResult.teacher_id)}
          >
            Assign {checkedResult.name}
          </Button>
        )}
        {Boolean(selectedTeacherId) && (
          <Button
            color="black"
            loading={isAssigning}
            disabled={isAssigning}
            onClick={() => handleAssign(selectedTeacherId)}
          >
            Assign From List
          </Button>
        )}
      </>
    );
  };

  return (
    <Modal open size="small" onClose={closeModal}>
      <ModalHeader>
        {isCoachChange ? 'Change coach' : 'Assign a coach'}
      </ModalHeader>
      <ModalContent>
        {!grade ? (
          renderLoading()
        ) : (
        <ModalDescription>
          <ContextCard gutter={0}>
            <ContextRow gutter={0}>
              <Text variant="body2">
                Grade {grade} · {failure.region_code || '-'} ·{' '}
                {effectiveTier || 'no tier'} · waiting{' '}
                {formatAge(failure.age_hours)}
                {failure.attempt_count > 1
                  ? ` · ${failure.attempt_count} attempts`
                  : ''}
              </Text>
            </ContextRow>
            {!isCoachChange && (
              <ContextRowTight gutter={0}>
                <Text variant="body3" color={COLORS.GRAY}>
                  {reason.label} — {reason.detail}
                </Text>
              </ContextRowTight>
            )}
            <ContextRowTight gutter={0}>
              <Text variant="body3" color={COLORS.GRAY}>
                Booked slots:{' '}
                {(failure.requested_slots_ist || []).length
                  ? (failure.requested_slots_ist || []).join('   ')
                  : 'none chosen yet'}
              </Text>
            </ContextRowTight>
            {/* A mapped row has no failure funnel; showing dashes reads as broken. */}
            {!isCoachChange && (
              <ContextRowTight gutter={0}>
                <Text variant="body3" color={COLORS.GRAY}>
                  {FUNNEL_STEPS.map(step => {
                    const value = (failure.funnel_counts || {})[step.key];

                    return `${step.label} ${value === undefined ? '-' : value}`;
                  }).join('  →  ')}
                </Text>
              </ContextRowTight>
            )}
          </ContextCard>

          {editingSlots && (
            <>
          <Separator height={12} transparent />
          <Text variant="body2">Slots to check (IST)</Text>
          <Separator height={2} transparent />
          <Text variant="body3" color={COLORS.GRAY}>
            Prefilled with what the parent booked. Changing one re-checks who is free
            at the new time; the coach list below follows, and the assign stores the
            new slots.
          </Text>
          <Separator height={6} transparent />
          {slots.map((value, index) => (
            <SlotField key={`slot-${index}`}>
              <HalfHourPicker
                value={value}
                onChange={next =>
                  setSlots(current =>
                    current.map((slot, at) => (at === index ? next : slot)),
                  )
                }
                ariaLabel={`Slot ${index + 1}`}
              />
            </SlotField>
          ))}
          <Separator height={4} transparent />
          <Row alignItems="center" style={{ gap: '10px' }}>
            <Button
              size="tiny"
              color="black"
              disabled={
                hasPastSlot ||
                !slotEpochs.length ||
                slotEpochs.length < slots.length
              }
              onClick={() => setConfirmedSlots(slotEpochs)}
            >
              Confirm slots &amp; find coaches
            </Button>
            {slotsConfirmed && (
              <Button size="tiny" basic onClick={() => setConfirmedSlots(null)}>
                Reset to booked
              </Button>
            )}
          </Row>
          <Separator height={4} transparent />
          {hasPastSlot && (
            <Text variant="body3" color={COLORS.RUSTY_RED}>
              One of these slots is in the past. Pick times from now onwards.
            </Text>
          )}
          {slotsConfirmed ? (
            <Text variant="body3" color={COLORS.YELLOW_4}>
              Coaches below are checked against these slots, and assigning stores
              them.
            </Text>
          ) : (
            <Text variant="body3" color={COLORS.GRAY}>
              {slotsChanged
                ? 'Edited but not applied — confirm to re-check availability.'
                : 'Coaches below are checked against the booked slots.'}
            </Text>
          )}
            </>
          )}

          {needsTier && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2">Plan tier</Text>
              <Separator height={2} transparent />
              <Text variant="body3" color={COLORS.GRAY}>
                Grade {grade} is covered by both K-5 and 3-8 coaches, so the
                tier is what picks the licence band.
              </Text>
              <Separator height={6} transparent />
              <Dropdown
                selection
                compact
                selectOnBlur={false}
                options={PLAN_TIER_OPTIONS}
                value={effectiveTier}
                placeholder="Select a tier"
                onChange={(_, { value }) => setTier(value)}
                aria-label="Plan tier"
              />
            </>
          )}

          <Separator height={16} transparent />
          {renderBody()}

          {assignError && (
            <>
              <Separator height={12} transparent />
              <Text variant="body2" color={COLORS.RUSTY_RED}>
                {assignError}
              </Text>
            </>
          )}
        </ModalDescription>
        )}
      </ModalContent>
      <ModalActions>{renderActions()}</ModalActions>
    </Modal>
  );
};

export default AssignCoach;
